import { useDocumentTitle } from '../app/useDocumentTitle';
import { TEMPLATE_IDS } from '@miroom/shared';
import { Download, LayoutTemplate, MousePointer2, Share2, Sparkles, Vote } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../app/useAuth';
import { Logo } from '../components/Logo';
import { PrefsSwitcher } from '../components/PrefsSwitcher';
import { UserMenu } from '../components/UserMenu';
import { useT } from '../i18n/i18n';
import { TemplateGallery } from './dashboard/TemplatesModal';
import styles from './LandingPage.module.css';
import { useNavigate } from 'react-router-dom';

const FEATURES = [
  { key: 'realtime', icon: MousePointer2 },
  { key: 'tools', icon: Sparkles },
  { key: 'facilitation', icon: Vote },
  { key: 'templates', icon: LayoutTemplate },
  { key: 'share', icon: Share2 },
  { key: 'export', icon: Download },
] as const;

export function LandingPage() {
  const t = useT();
  useDocumentTitle(null);
  const { status } = useAuth();
  const navigate = useNavigate();
  const authed = status === 'authenticated';
  const primary = authed ? '/boards' : '/register';

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <Logo />
        <nav className={styles.nav}>
          <PrefsSwitcher />
          {authed ? (
            <UserMenu />
          ) : (
            <>
              <Link to="/login" className={styles.navLink}>
                {t('nav.login')}
              </Link>
              <Link to="/register" className={styles.navCta}>
                {t('nav.register')}
              </Link>
            </>
          )}
        </nav>
      </header>

      <main>
        <section className={styles.hero}>
          <div className={styles.heroText}>
            <h1>{t('landing.title')}</h1>
            <p>{t('landing.subtitle')}</p>
            <div className={styles.ctas}>
              <Link to={primary} className={styles.ctaPrimary}>
                {authed ? t('landing.ctaBoards') : t('landing.cta')}
              </Link>
              {!authed && (
                <Link to="/login" className={styles.ctaSecondary}>
                  {t('landing.login')}
                </Link>
              )}
            </div>
          </div>
          <div className={styles.demo} aria-hidden="true">
            <div className={`${styles.note} ${styles.n1}`}>{t('landing.demo.idea')}</div>
            <div className={`${styles.note} ${styles.n2}`}>{t('landing.demo.vote')} 🗳</div>
            <div className={`${styles.note} ${styles.n3}`}>{t('landing.demo.launch')} 🚀</div>
            <div className={styles.shape}>{t('landing.demo.plan')}</div>
            <svg className={styles.arrow} viewBox="0 0 200 80">
              <path
                d="M5 70 C 60 70, 120 10, 190 12"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                markerEnd="url(#lh)"
              />
              <defs>
                <marker
                  id="lh"
                  viewBox="0 0 10 10"
                  refX="9"
                  refY="5"
                  markerWidth="6"
                  markerHeight="6"
                  orient="auto"
                >
                  <path d="M0,0 L10,5 L0,10 Z" fill="currentColor" />
                </marker>
              </defs>
            </svg>
            <div className={`${styles.cursor} ${styles.c1}`}>
              <svg width="18" height="22" viewBox="0 0 18 22">
                <path
                  d="M1 1 L1 17 L5.5 13 L8.5 20.5 L11.5 19 L8.5 12 L15 12 Z"
                  fill="#be185d"
                  stroke="#fff"
                  strokeWidth="1.5"
                />
              </svg>
              <span style={{ background: '#be185d' }}>Аня</span>
            </div>
            <div className={`${styles.cursor} ${styles.c2}`}>
              <svg width="18" height="22" viewBox="0 0 18 22">
                <path
                  d="M1 1 L1 17 L5.5 13 L8.5 20.5 L11.5 19 L8.5 12 L15 12 Z"
                  fill="#0369a1"
                  stroke="#fff"
                  strokeWidth="1.5"
                />
              </svg>
              <span style={{ background: '#0369a1' }}>Max</span>
            </div>
          </div>
        </section>

        <section className={styles.why} aria-labelledby="why-title">
          <h2 id="why-title">{t('landing.why.title')}</h2>
          <p className={styles.formula} aria-label={t('landing.why.formulaLabel')}>
            <span className={styles.part}>Miro</span>
            <span className={styles.op}>+</span>
            <span className={styles.part}>Room</span>
            <span className={styles.op}>=</span>
            <span className={`${styles.part} ${styles.result}`}>miroom</span>
          </p>
          <p className={styles.whyText}>{t('landing.why.text')}</p>
        </section>

        <section className={styles.features} aria-labelledby="features-title">
          <h2 id="features-title">{t('landing.featuresTitle')}</h2>
          <ul>
            {FEATURES.map(({ key, icon: Icon }) => (
              <li key={key}>
                <span className={styles.featureIcon}>
                  <Icon size={22} />
                </span>
                <h3>{t(`landing.features.${key}.title`)}</h3>
                <p>{t(`landing.features.${key}.text`)}</p>
              </li>
            ))}
          </ul>
        </section>

        <section className={styles.templates} aria-labelledby="templates-title">
          <h2 id="templates-title">{t('landing.templatesTitle')}</h2>
          <TemplateGallery
            ids={TEMPLATE_IDS.slice(1, 9)}
            busy={null}
            onPick={() => navigate(primary)}
          />
        </section>

        <section className={styles.bottom}>
          <h2>{t('landing.bottomTitle')}</h2>
          <p>{t('landing.bottomText')}</p>
          <Link to={primary} className={styles.ctaPrimary}>
            {authed ? t('landing.ctaBoards') : t('landing.cta')}
          </Link>
        </section>
      </main>

      <footer className={styles.footer}>
        <Logo size={22} />
        <span>{t('landing.footer')}</span>
        <span>© {new Date().getFullYear()}</span>
      </footer>
    </div>
  );
}
